import { Box, ButtonProps, Divider, Stack, styled } from '@mui/material';
import { CheckmarkIcon, ShoppingCartIcon, SingleInquiryIcon } from '@synergy/theme-next';
import { DeliveryStatus } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { MouseEvent, ReactElement, useState } from 'react';
import {
    PoolButton,
    PoolCardContent,
    PoolCardContentProps,
    PoolLineClampTypography,
    PoolTypography,
    useIsMobile,
} from '@synergy/components-next';

import { ArticleCardContentArticleNumber } from './article-card-content-article-number';
import { ArticleCardContentContentInfo } from '../components/article-card-content-content-info';
import { ArticleCardContentPriceInfo } from '../components/article-card-content-price-info';
import { ArticleCardContentSupplierInfo } from '../components/article-card-content-supplier-info';
import { SearchScoreOverlay } from '../search-score-overlay';
import { useArticleCardContext } from '../article-card';

export interface ArticleCardContentProps extends PoolCardContentProps {
    showButton: boolean;
}

const FullWidthDivider = styled(Divider)(({ theme }) => ({
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    borderColor: theme.palette.grey[200],
}));

const SlidingContent = styled(PoolCardContent, {
    shouldForwardProp: prop => prop !== 'showButton',
})<{ showButton: boolean }>(({ theme, showButton }) => ({
    height: '100%',
    position: 'relative',
    transform: showButton ? 'translateY(-60px)' : 'none',
    transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    transitionDelay: showButton ? '200ms' : '0',
    backgroundColor: theme.palette.common.white,
    paddingTop: theme.spacing(1.5),
}));

const FadingButton = styled(PoolButton, {
    shouldForwardProp: props => props !== 'showButton',
})<ButtonProps & { showButton: boolean }>(({ showButton }) => ({
    opacity: showButton ? 1 : 0,
    transition: 'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    transitionDelay: showButton ? '200ms' : '0',
}));

export const ArticleCardContent = (props: ArticleCardContentProps): ReactElement => {
    const isDesktop = !useIsMobile();
    const [addedToShoppingCart, setAddedToShoppingCart] = useState(false);
    const { article, onAddToShoppingCart, onInquiryClick } = useArticleCardContext();

    const handleAddToShoppingCartClick = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        if (addedToShoppingCart || !onAddToShoppingCart) {
            return;
        }
        onAddToShoppingCart();
        setAddedToShoppingCart(true);
    };

    const handleSingleInquiry = (e: MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        onInquiryClick?.();
    };

    return (
        <SlidingContent showButton={isDesktop && props.showButton}>
            <Stack>
                <ArticleCardContentArticleNumber />

                <FullWidthDivider />

                <Stack sx={{ minHeight: 172 }} gap={1}>
                    <PoolLineClampTypography
                        variant="subtitle1"
                        component="span"
                        maxLines={2}
                        sx={{
                            mt: 1.5,
                            fontWeight: 600,
                            minHeight: 44,
                        }}
                    >
                        {article.name}
                    </PoolLineClampTypography>

                    <Box>
                        {article.deliveryStatus !== DeliveryStatus.OnRequest ? (
                            <>
                                <ArticleCardContentPriceInfo />
                                <ArticleCardContentContentInfo />
                            </>
                        ) : (
                            <PoolTypography variant="h5" translationId="app.article.priceOnRequest" />
                        )}
                    </Box>
                    <ArticleCardContentSupplierInfo />
                </Stack>
                {isDesktop && (
                    <Box sx={{ position: 'absolute', left: 16, right: 16, bottom: -40 }}>
                        {article.canBePurchased ? (
                            <FadingButton
                                showButton={props.showButton}
                                fullWidth={true}
                                onClick={handleAddToShoppingCartClick}
                                startIcon={addedToShoppingCart ? <CheckmarkIcon /> : <ShoppingCartIcon />}
                                data-testid="add-to-cart"
                            >
                                {addedToShoppingCart ? (
                                    <FormattedMessage id="app.common.added" />
                                ) : (
                                    <FormattedMessage id="app.article.shoppingCart.add" />
                                )}
                            </FadingButton>
                        ) : (
                            <FadingButton
                                startIcon={<SingleInquiryIcon />}
                                showButton={props.showButton}
                                fullWidth={true}
                                onClick={handleSingleInquiry}
                            >
                                <FormattedMessage id="app.singleInquiry" />
                            </FadingButton>
                        )}
                    </Box>
                )}
            </Stack>

            <SearchScoreOverlay score={article.score || 0} />
        </SlidingContent>
    );
};
